import { createStyles, Stack } from '@mantine/core';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { isEmpty, isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { ButtonDetails } from '../../../components/EmptyState';
import { EmptyState } from '../../../components/EmptyState';
import type { IInboxViewProps } from '../types';
import { InboxViewBody } from './InboxViewBody';
import { InboxViewHeader } from './InboxViewHeader';

const useStyles = createStyles(() => ({
	container: {
		width: '100%',
		height: '100vh',
		display: 'flex',
		flexGrow: 1,
		alignItems: 'center',
	},
}));

export function InboxView({
	selected,
	setSelectedId,
	data,
	searchTerm,
	events,
	isLoading,
	inboxEventTypes,
	deleteNotification,
	onKeyDown,
	applyChangeRequest,
	applyAccessRequest,
	incident,
	onUpdateIncident,
	commentActions,
	handleDeleteKey,
	openEditKeyModal,
	changeRequestApplied,
	accessRequestApplied,
	user,
	workspace,
	isCompleteAutomationReviewLoading,
	completeAutomationReviewMutation,
	embeddedPrompt,
	isLoadingEmbeddedPrompt,
	approveTeamAccessRequest,
	rejectTeamAccessRequest,
	isApprovingTeamAccessRequest,
	isRejectingTeamAccessRequest,
}: IInboxViewProps) {
	const { classes } = useStyles();

	const navigate = useNavigate();

	const handleClearFilters = useCallback(() => {
		navigate('/inbox');
	}, [navigate]);

	const buttons: ButtonDetails[] = useMemo(
		() => [
			{
				name: 'Clear filters',
				action: handleClearFilters,
				isPrimary: false,
				size: 'md',
			},
		],
		[handleClearFilters]
	);

	if ((!isEmpty(events) || !isEmpty(searchTerm)) && isEmpty(data)) {
		return (
			<EmptyState
				iconName="inbox"
				title="No notifications found"
				description="Try searching for something else or clearing filters"
				buttons={buttons}
				includeGoBack={false}
				size="sm"
			/>
		);
	}

	if (isEmpty(data)) {
		return (
			<EmptyState
				iconName="inbox"
				title="You're all caught up"
				description="Check back later for new notifications."
				includeGoBack={false}
				size="sm"
			/>
		);
	}

	if (isNil(selected) && !isLoading) {
		return (
			<EmptyState
				iconName="inbox"
				title="No notification selected"
				description="Select a notification from the sidebar."
				includeGoBack={false}
				size="sm"
			/>
		);
	}

	return (
		<Stack className={classes.container} spacing={0}>
			<InboxViewHeader
				selected={selected}
				setSelectedId={setSelectedId}
				data={data}
				inboxEventTypes={inboxEventTypes}
				onKeyDown={onKeyDown}
				deleteNotification={deleteNotification}
				applyChangeRequest={applyChangeRequest}
				applyAccessRequest={applyAccessRequest}
				changeRequestApplied={changeRequestApplied}
				accessRequestApplied={accessRequestApplied}
				incident={incident}
				onUpdateIncident={onUpdateIncident}
				user={user}
				workspace={workspace}
				isCompleteAutomationReviewLoading={isCompleteAutomationReviewLoading}
				completeAutomationReviewMutation={completeAutomationReviewMutation}
				approveTeamAccessRequest={approveTeamAccessRequest}
				rejectTeamAccessRequest={rejectTeamAccessRequest}
				isApprovingTeamAccessRequest={isApprovingTeamAccessRequest}
				isRejectingTeamAccessRequest={isRejectingTeamAccessRequest}
			/>
			<InboxViewBody
				selected={selected}
				isLoading={isLoading}
				commentActions={commentActions}
				handleDeleteKey={handleDeleteKey}
				openEditKeyModal={openEditKeyModal}
				isCompleteAutomationReviewLoading={isCompleteAutomationReviewLoading}
				completeAutomationReviewMutation={completeAutomationReviewMutation}
				embeddedPrompt={embeddedPrompt}
				isLoadingEmbeddedPrompt={isLoadingEmbeddedPrompt}
			/>
		</Stack>
	);
}
